/**
 * VuMediChatbot is an AI chatbot intended to provide answers and insights to doctors.
 *
 * @see [STEAM-314](https://vumedi.atlassian.net/browse/STEAM-314)
 *
 * [USAGE]

```scss
// Usage code inside a view style file where AI chatbot should be displayed.
@use "~Styles/components/chatbot";

.vm-chatbot {
    // Rules to define parent component for both trigger and suggestions
}

.vm-chatbot-trigger {
    // Rules to define placement and size of the chatbot trigger component
}

.vm-chatbot-suggestions {
   // Rules to define placement and size of the chatbot suggestions
}
```

```javascript
// Usage code inside a view JS file where AI chatbot should be displayed.
import { VuMediChatbot } from 'Utils/vmChatbot';

const triggerContainer = document.querySelector('.vm-chatbot'); // Place for CTA and suggestions
const chatbot = new VuMediChatbot(browseSlug, channelSlug);     // It's enough to provide only one slug, browseSlug is preferred

// Initialize and render chatbot trigger element inside a specified container:
//
// * Will remove `hidden` class from the container.
// * Won't render if user is not allowed to use the chatbot.
// * Predefined questions will be automatically fetched and rendered.
await chatbot.initialize(triggerContainer);
```
 */
import * as Sentry from '@sentry/browser';
import { parse } from 'marked';

import { buildURL, fetchRequest } from 'Utils/apiFetchRequest';

const Copy = {
    placeholder: {
        default: 'Ask Vumedi AI...',
        loading: 'Searching...',
    },
    trigger: 'Ask Vumedi AI',
    error: {
        questionResponse: 'Sorry, I could not compute.',
    },
};

export class VuMediChatbot {
    constructor(browseSlug = '', channelSlug = '') {
        this.browseSlug = browseSlug;
        this.channelSlug = channelSlug;

        this.triggerElement = null;
        this.suggestionsElement = null;
        this.modalElement = null;
        this.uuid = null;
        this.inProgress = false;
    }

    get slugParameter() {
        if (this.browseSlug) {
            return { browse: this.browseSlug };
        }

        if (this.channelSlug) {
            return { channel: this.channelSlug };
        }

        return {};
    }

    _askQuestion(question) {
        if (!this.modalElement) {
            return;
        }

        this.inProgress = true;

        this.modalElement.querySelector('.vm-chatbot-body').classList.add('vm-chatbot-body--conversation');
        this.modalElement.querySelector('input[type="text"]').value = '';
        this.modalElement.querySelector('input[type="text"]').setAttribute('placeholder', Copy.placeholder.loading);
        this.modalElement.querySelector('button[type="submit"]').setAttribute('disabled', 'disabled');

        this._renderQuestion(question);

        const options = {
            method: 'POST',
            body: JSON.stringify({ question }),
        };

        fetchRequest(`/api/chats/${this.uuid}/ask/`, options)
            .then((data) => {
                if (typeof data?.answer === 'string' && data.answer.length) {
                    this._renderAnswer(data.answer);
                    return;
                }

                this._renderAnswer(Copy.error.questionResponse);
            })
            .catch((error) => {
                this._renderAnswer(Copy.error.questionResponse);
                Sentry.captureException(error);
            })
            .finally(() => {
                this.inProgress = false;

                if (this.modalElement?.hasAttribute('open')) {
                    this.modalElement.querySelector('button[type="submit"]').removeAttribute('disabled');
                    this.modalElement
                        .querySelector('input[type="text"]')
                        .setAttribute('placeholder', Copy.placeholder.default);
                }
            });
    }

    _handleCloseClick() {
        if (!this.modalElement) {
            return;
        }

        this.modalElement.close();
        this.modalElement.querySelector('.vm-chatbot-body').classList.remove('vm-chatbot-body--conversation');
        document.body.style.overflow = '';

        const messages = this.modalElement.querySelector('.vm-chatbot-messages');

        while (messages.firstChild) {
            messages.removeChild(messages.firstChild);
        }
    }

    _handleTriggerClick(question = '') {
        if (!this.modalElement) {
            return;
        }

        this.modalElement.setAttribute('data-state', 'loading');
        this.modalElement.showModal();
        document.body.style.overflow = 'hidden';

        const options = {
            method: 'POST',
            body: JSON.stringify(this.slugParameter),
        };

        fetchRequest('/api/chats/', options)
            .then((data) => {
                if (!data?.uuid) {
                    Sentry.captureException(new Error('Invalid response from the server'));
                    return;
                }

                this.uuid = data.uuid;
                this.inProgress = false;
                this.modalElement.setAttribute('data-state', 'ready');
                this.modalElement.querySelector('button[type="submit"]').removeAttribute('disabled');
                this.modalElement.querySelector('input[type="text"]').focus();
                this.modalElement
                    .querySelector('input[type="text"]')
                    .setAttribute('placeholder', Copy.placeholder.default);

                if (question && this.modalElement) {
                    this.modalElement.querySelector('input[type="text"]').value = question;
                    this.modalElement.querySelector('button[type="submit"]').click();
                }
            })
            .catch((error) => {
                this.modalElement?.close();
                this.modalElement?.removeAttribute('data-state');
                document.body.style.overflow = '';
                Sentry.captureException(error);
            });
    }

    _renderModalElement() {
        const markup = `<dialog class="vm-chatbot-container">
            <span class="fa fa-spinner fa-pulse fa-2x fa-fw vm-chatbot-loading"></span>
            <div class="vm-chatbot-modal">
                <div class="vm-chatbot-header">
                    <h3>Vumedi <strong>AI<i class="icon icon-chatbot-minimal"></i></strong></h3>
                    <button type="button" class="vm-button vm-button--sm vm-button--tertiary vm-chatbot-close">
                        <i class="icon icon-x"></i>
                    </button>
                </div>
                <div class="vm-chatbot-body">
                    <div class="vm-chatbot-placeholder">This is a placeholder for the chatbot landing screen.</div>
                    <div class="vm-chatbot-messages"></div>
                </div>
                <div class="vm-chatbot-footer">
                    <form>
                        <i class="icon icon-chatbot"></i>
                        <input type="text"
                               name="question"
                               class="vm-chatbot-input"
                               autocomplete="off"
                               placeholder="${Copy.placeholder.default}"
                               aria-label="Question for AI" />
                        <button type="submit" class="vm-button vm-button--sm vm-button--primary vm-chatbot-send">
                            <i class="icon icon-arrow-up"></i>
                        </button>
                    </form>
                </div>
            </div>
        </dialog>`;

        const template = document.createElement('template');
        template.innerHTML = markup;

        const containerElement = template.content.firstChild;

        containerElement.querySelector('.vm-chatbot-close').addEventListener('click', (ev) => {
            ev.preventDefault();
            this._handleCloseClick();
        });

        containerElement.querySelector('form').addEventListener('submit', (ev) => {
            ev.preventDefault();

            if (this.inProgress) {
                return;
            }

            const inputElement = containerElement.querySelector('input[type="text"]');
            const question = inputElement.value.trim();

            if (!question) {
                inputElement.value = '';
                return;
            }

            this._askQuestion(question);
        });

        return containerElement;
    }

    _renderQuestion(question) {
        if (!this.modalElement) {
            return;
        }

        const questionElement = document.createElement('div');
        questionElement.classList.add('vm-chatbot-message', 'vm-chatbot-message--question');
        questionElement.innerText = question;

        const iconElement = document.createElement('i');
        iconElement.classList.add('icon', 'icon-chatbot');
        questionElement.prepend(iconElement);

        const messagesElement = this.modalElement.querySelector('.vm-chatbot-messages');
        messagesElement.appendChild(questionElement);
        messagesElement.scrollTo({
            top: messagesElement.scrollHeight,
            behavior: 'smooth',
        });
    }

    _renderAnswer(answer) {
        if (!this.modalElement) {
            return;
        }

        const answerElement = document.createElement('div');
        answerElement.classList.add('vm-chatbot-message', 'vm-chatbot-message--answer');
        answerElement.innerHTML = parse(answer);

        const messagesElement = this.modalElement.querySelector('.vm-chatbot-messages');
        messagesElement.appendChild(answerElement);

        // Scroll so the last question and beginning of the answer are visible
        const lastQuestionElement = (() => {
            const questions = messagesElement.querySelectorAll('.vm-chatbot-message--question');

            return questions[questions.length - 1];
        })();

        const spacing = 6 * 24;

        messagesElement.scrollTo({
            top: Math.max(
                messagesElement.scrollHeight - answerElement.scrollHeight - lastQuestionElement.scrollHeight - spacing,
                0,
            ),
            behavior: 'smooth',
        });
    }

    /**
     * Render a single suggested question.
     *
     * @param { id: number, question: string } question
     */
    _renderPredefined(question) {
        if (!this.suggestionsElement) {
            return;
        }

        const suggestionElement = document.createElement('button');
        suggestionElement.setAttribute('type', 'button');
        suggestionElement.setAttribute('id', question.id);
        suggestionElement.classList.add('vm-chatbot__suggestion');

        const iconElement = document.createElement('i');
        iconElement.classList.add('icon', 'icon-chatbot');

        suggestionElement.innerText = question.question;
        suggestionElement.prepend(iconElement);

        suggestionElement.addEventListener('click', (ev) => {
            ev.preventDefault();
            this._handleTriggerClick(question.question);
        });

        this.suggestionsElement.appendChild(suggestionElement);
    }

    _renderSuggestionsElement() {
        const containerElement = document.createElement('div');
        containerElement.classList.add('vm-chatbot__suggestions');

        return containerElement;
    }

    _renderTriggerElement() {
        const betaElement = document.createElement('span');
        betaElement.classList.add('vm-chatbot__trigger-badge');
        betaElement.innerText = 'Beta';

        const textElement = document.createElement('span');
        textElement.classList.add('vm-chatbot__trigger-text');
        textElement.innerText = Copy.trigger;

        const iconElement = document.createElement('i');
        iconElement.classList.add('icon', 'icon-chatbot');

        textElement.prepend(iconElement);

        const triggerElement = document.createElement('button');
        triggerElement.setAttribute('type', 'button');
        triggerElement.classList.add('vm-chatbot__trigger');

        triggerElement.appendChild(betaElement);
        triggerElement.appendChild(textElement);

        triggerElement.addEventListener('click', (ev) => {
            ev.preventDefault();
            this._handleTriggerClick();
        });

        return triggerElement;
    }

    _render(triggerContainer, predefined = []) {
        const contentElement = document.createElement('div');
        contentElement.classList.add('vm-chatbot__content');

        this.triggerElement = this._renderTriggerElement();
        this.suggestionsElement = this._renderSuggestionsElement();
        this.modalElement = this._renderModalElement();

        const spacerElement = document.createElement('div');
        spacerElement.classList.add('vm-chatbot__spacer');

        contentElement.appendChild(this.triggerElement);
        contentElement.appendChild(spacerElement);
        contentElement.appendChild(this.suggestionsElement);
        contentElement.appendChild(this.modalElement);

        triggerContainer.appendChild(contentElement);

        predefined.forEach((question) => this._renderPredefined(question));
    }

    /**
     * Retrieve predefined questions for the provided browse slug.
     *
     * @returns Promise<{ status: 'error|forbidden|success', predefined: Array<{ question: string }> }>
     */
    _retrievePredefined() {
        return new Promise((resolve) => {
            const endpoint = buildURL('/api/chats/predefined-questions/', this.slugParameter);

            fetchRequest(endpoint)
                .then((data) => {
                    if (!Array.isArray(data?.results)) {
                        Sentry.captureException(new Error('Invalid response from the server'));
                        resolve({ status: 'error', predefined: [] });
                        return;
                    }

                    resolve({ status: 'success', predefined: data.results });
                })
                .catch((error) => {
                    if (error?.message === 'Forbidden') {
                        resolve({ status: 'forbidden', predefined: [] });
                        return;
                    }

                    Sentry.captureException(error);
                    resolve({ status: 'error', predefined: [] });
                });
        });
    }

    async initialize(triggerContainer) {
        const { status, predefined } = await this._retrievePredefined();

        if (status === 'forbidden' || status === 'error') {
            return;
        }

        triggerContainer.classList.remove('hidden');

        this._render(triggerContainer, predefined);
    }
}

/**
 * Extract browse term from the URL. Works on channel and disease pages.
 *
 * If browse term cannot be found, returns null.
 *
 * @returns {Object|null}
 */
export function extractSlug() {
    const pathName = window.location.pathname;
    const pathUnits = pathName.split('/').filter((x) => !!x);

    if (pathName.startsWith('/term/')) {
        return { browse: pathUnits.pop() };
    }

    if (pathName.startsWith('/channel/')) {
        const slug = pathUnits.pop();

        if (slug === 'browse') {
            return null;
        }

        return { channel: slug };
    }

    return null;
}
